import { useState, useRef, useEffect } from 'react'
import { renderMarkdown } from './utils/markdown'
import { streamOpenAIResponse } from './services/openai'
import { streamAnthropicResponse } from './services/anthropic'
import LoginOverlay from './components/LoginOverlay'
import hljs from 'highlight.js'
import { onAuthStateChanged, getAuth, signOut } from 'firebase/auth'
import { auth } from './config/firebase'
import demoContent from './content/demoStrategy.md?raw';
import TokenBar from './components/TokenBar';
import logo from './assets/logo.svg';
import { useLocation, useSearchParams } from 'react-router-dom';
import PaymentSuccessOverlay from './components/PaymentSuccessOverlay';
import ThreadHistory from './components/ThreadHistory';
import historyIcon from './assets/align-right-w.svg';

// OpenAI API configuration
const OPENAI_CONFIG = {
  model: 'gpt-4',
  temperature: 0.7,      // Creativity vs determinism (0-1)
  presence_penalty: 0.1, // Encourage topic diversity
  frequency_penalty: 0.1  // Reduce repetition
}

// Update the default prompt constant at the top
const DEFAULT_PROMPT = "Write a guide on business strategy development. Include markdown tables for frameworks and a NVIDIA strategy Wardley map using the onlinewardleymaps.code syntax. Use headers, lists, and formatting for clarity."

function App() {
  const [messages, setMessages] = useState([
  ])
  const [input, setInput] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage first, default to true if not set
    const stored = localStorage.getItem('darkMode')
    return stored === null ? true : stored === 'true'
  })
  const [useProxy, setUseProxy] = useState(() => {
    const stored = localStorage.getItem('useProxy')
    // Force proxy if VITE_OPENAI_API_KEY is 'PROXY'
    return import.meta.env.VITE_OPENAI_API_KEY === 'PROXY' ? true : 
           stored === null ? false : stored === 'true'
  })
  const [currentThreadId, setCurrentThreadId] = useState(null)
  const messagesEndRef = useRef(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState(null)
  const [showLogin, setShowLogin] = useState(true)  // Start with login shown
  const [user, setUser] = useState(null)
  // Parse services from env, removing quotes if present
  // console.log('Raw env value:', import.meta.env.VITE_LLM_SERVICES)
  const llmServices = (import.meta.env.VITE_LLM_SERVICES || '')
    .replace(/"/g, '')  // Remove quotes
    .split(',')
    .map(s => s.trim())
    .filter(Boolean) || ['ChatGPT 4o']  // Default if empty

  // Map of display names to env var names
  const serviceToEnvMap = {
    'ChatGPT 4o': 'OPENAI_API_KEY',
    'Claude 3.5 Sonnet': 'ANTHROPIC_API_KEY'
  }

  // Ensure OpenAI is the default if present in the list
  const sortedServices = llmServices.includes('ChatGPT 4o') 
    ? ['ChatGPT 4o', ...llmServices.filter(s => s !== 'ChatGPT 4o')]
    : llmServices

  // console.log('Processed services:', sortedServices)
  const [selectedService, setSelectedService] = useState(sortedServices[0] || 'ChatGPT 4o')

  // Reset token counts when service changes
  const handleServiceChange = (e) => {
    setSelectedService(e.target.value);
    setTokenCounts({ input: 0, output: 0 }); // Reset counts on service change
  }

  // Load API key on mount
  useEffect(() => {
    const envApiKey = import.meta.env.VITE_OPENAI_API_KEY
    if (envApiKey?.startsWith('sk-')) {
      setApiKey(envApiKey)
      setIsInitializing(false)
      return
    }
    
    const storedKey = localStorage.getItem('openai_api_key')
    if (storedKey?.startsWith('sk-')) {
      setApiKey(storedKey)
    }
    setIsInitializing(false)
  }, [])

  // Handle API key changes
  const handleApiKeyChange = (newKey) => {
    try {
      setApiKey(newKey)
      if (newKey) {
        localStorage.setItem('openai_api_key', newKey)
      } else {
        localStorage.removeItem('openai_api_key')
      }
    } catch (error) {
      console.error('Error saving API key:', error)
      // Clear everything on error
      setApiKey('')
      localStorage.removeItem('openai_api_key')
    }
  }

  // Clear API key safely
  const clearApiKey = () => {
    setApiKey('')
    localStorage.removeItem('openai_api_key')
  }

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode)
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [darkMode])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Re-run syntax highlighting after content updates
  useEffect(() => {
    if (!isSending) {  // Only highlight after streaming is done
      document.querySelectorAll('pre code').forEach((block) => {
        hljs.highlightElement(block);
      });
    }
  }, [messages, isSending]);

  // Add auth state listener
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (!user) {
        setShowLogin(true);
        // Reset to demo prompt when logged out
        const { prompt } = getDemoContent();
        setInput(prompt);
      } else {
        // Clear input when logged in
        setInput('');
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      setMessages([]);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Extract prompt and content from demo file
  const getDemoContent = () => {
    const promptMatch = demoContent.match(/# Prompt: (.+?)\n/);
    const prompt = promptMatch ? promptMatch[1] : '';
    const content = demoContent.replace(/# Prompt: .+?\n/, '');
    return { prompt, content };
  };

  // Initialize input with demo prompt
  useEffect(() => {
    const { prompt } = getDemoContent();
    setInput(prompt);
  }, []);

  const handleClearInput = () => {
    if (!user) {
      setShowLogin(true);
    } else {
      setInput('');
    }
  };

  // Add token count state
  const [tokenCounts, setTokenCounts] = useState({ input: 0, output: 0 })

  const [userTokens, setUserTokens] = useState({
    freeLimit: 0,
    purchasedTokens: 0,
    lifetimeTotal: 0
  });

  // Add token status fetch function
  const fetchTokenStatus = async () => {
    if (!user) return;
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/token-status`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUserTokens({
          freeLimit: data.free_limit,
          purchasedTokens: data.purchased_tokens || 0,
          lifetimeTotal: data.lifetime_total
        });
      }
    } catch (error) {
      console.error('Error fetching token status:', error);
    }
  };

  // Add effect to fetch token status on login
  useEffect(() => {
    if (user) {
      fetchTokenStatus();
    }
  }, [user]);

  // Function to handle thread selection from history
  const handleThreadSelect = (thread) => {
    if (!thread || !thread.messages) return;
    
    // Update messages state with the thread's messages
    setMessages(thread.messages);
    
    // Set the current thread ID
    setCurrentThreadId(thread.thread_id);
    
    // Scroll to the bottom after messages are updated
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
    console.log('Thread loaded:', {
      threadId: thread.thread_id,
      messageCount: thread.messages.length
    });
  };
  
  // Initialize a new thread or clear the current thread
  const startNewThread = () => {
    setMessages([]);
    setCurrentThreadId(null);
  };

  // Function to handle message submission
  const handleSubmit = async () => {
    if (!user) {
      // Show demo content when not logged in
      const { content } = getDemoContent();
      const userMessage = { role: 'user', content: input };
      setMessages(prev => [...prev, userMessage]);
      
      // Simulate streaming by splitting content into chunks
      setIsSending(true);
      const chunks = content.split(/(?<=\n)/);
      let currentContent = '';
      
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
      
      for (const chunk of chunks) {
        await new Promise(resolve => setTimeout(resolve, 50));
        currentContent += chunk;
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1].content = currentContent;
          return newMessages;
        });
      }
      
      setIsSending(false);
      await fetchTokenStatus();
      return;
    }

    // For authenticated users, use default prompt if input is empty
    const promptToUse = input.trim() || DEFAULT_PROMPT;

    // Only check for API key if not in PROXY mode
    if ((!apiKey && import.meta.env.VITE_OPENAI_API_KEY !== 'PROXY') || isSending) return;

    try {
      setIsSending(true);
      const userMessage = { role: 'user', content: promptToUse };
      setMessages(prev => [...prev, userMessage]);
      setInput(''); // Clear input after sending

      // Create placeholder for assistant's message
      setMessages(prev => [...prev, { role: 'assistant', content: '' }])

      let currentContent = ''
      let buffer = ''

      // Reset token counts before starting the stream
      setTokenCounts({ input: 0, output: 0 });

      // Determine which service to use based on selection
      let serviceConfig = {
        apiKey: apiKey,
        useProxy: useProxy,
        user: user  // Pass the authenticated user object
      }

      // If using proxy, use the selected service's environment variable name
      if (useProxy) {
        serviceConfig.apiKey = serviceToEnvMap[selectedService]
      }

      const response = await (selectedService === 'ChatGPT 4o'
        ? streamOpenAIResponse(promptToUse, messages.slice(0, -1), serviceConfig)
        : streamAnthropicResponse(promptToUse, messages.slice(0, -1), serviceConfig))

      // Process stream in chunks for better performance
      for await (const chunk of response) {
        if (typeof chunk === 'string') {  // OpenAI content chunk
          // console.log('OpenAI chunk received:', { 
          //   chunk,
          //   bufferLength: buffer.length,
          //   hasNewline: chunk.includes('\n'),
          //   currentContentLength: currentContent.length
          // });
          buffer += chunk;
          currentContent += chunk;
        } else if (chunk.type === 'content_block_delta' && chunk.delta && chunk.delta.text) {  // Claude content chunk
          buffer += chunk.delta.text;
          currentContent += chunk.delta.text;
        } else if (chunk.type === 'tokens') {  // Token count event
          if (chunk.counts.input) {
            setTokenCounts(prev => ({ ...prev, input: chunk.counts.input }));
          }
          if (chunk.counts.output) {
            setTokenCounts(prev => ({ ...prev, output: chunk.counts.output }));
          }
        }

        // Update UI less frequently for better performance
        if (buffer.length >= 25 || (typeof chunk === 'string' && chunk.includes('\n'))) {
          // console.log('Updating UI during stream:', {
          //   bufferLength: buffer.length,
          //   currentContentLength: currentContent.length,
          //   isOpenAI: typeof chunk === 'string'
          // });
          setMessages(prev => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1].content = currentContent;
            return newMessages;
          });
          buffer = '';
        }
      }

      // Always do a final update to ensure we don't lose any content
      if (buffer || currentContent) {
        console.log('Final content update:', {
          bufferLength: buffer.length,
          currentContentLength: currentContent.length,
          buffer: buffer
        });
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1].content = currentContent;
          return newMessages;
        });
      }

      // Save or update thread after streaming completes
      try {
        const idToken = await user.getIdToken();
        const finalMessages = [...messages.slice(0, -1), { 
          role: 'assistant', 
          content: currentContent,
          service: selectedService
        }];
        
        if (currentThreadId) {
          // If we have a thread ID, update the existing thread
          const updateResponse = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/thread/${currentThreadId}/update`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
              'X-Service-Key': serviceToEnvMap[selectedService]
            },
            body: JSON.stringify({
              messages: finalMessages
            })
          });

          if (!updateResponse.ok) {
            console.error('Failed to update thread:', await updateResponse.text());
            // If update fails, try creating a new thread instead
            await createNewThread(idToken, finalMessages);
          } else {
            console.log('Thread updated successfully');
          }
        } else {
          // Create a new thread
          await createNewThread(idToken, finalMessages);
        }
      } catch (error) {
        console.error('Error saving thread:', error);
      }

      await fetchTokenStatus();

    } catch (error) {
      console.error('API Error:', error)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${error.message || 'Unknown error occurred'}`
      }])
    } finally {
      setIsSending(false)
    }
  }

  // Helper function to create a new thread
  const createNewThread = async (idToken, messages) => {
    const threadResponse = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/thread`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        'X-Service-Key': serviceToEnvMap[selectedService]
      },
      body: JSON.stringify({
        userId: user.uid,
        email: user.email,
        messages: messages,
        metadata: {
          service: selectedService,
          timestamp: Date.now(),
          tokenCounts: {
            input: tokenCounts.input,
            output: tokenCounts.output,
            total: tokenCounts.input + tokenCounts.output
          }
        }
      })
    });

    if (threadResponse.ok) {
      // If successful, get the new thread ID
      const threadData = await threadResponse.json();
      if (threadData.success && threadData.thread_id) {
        setCurrentThreadId(threadData.thread_id);
        console.log('New thread created:', threadData.thread_id);
      }
    } else {
      console.error('Failed to create thread:', await threadResponse.text());
    }
  };

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  useEffect(() => {
    if (location.pathname === '/payment-success' && searchParams.get('session_id')) {
      setShowPaymentSuccess(true);
    }
  }, [location.pathname, searchParams]);

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Show loading state only during initialization
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="mb-4">Welcome to {import.meta.env.VITE_APP_TITLE || 'FoxyStrat'}</div>
          <div className="text-sm text-gray-400">
            Make sure the OpenAI API key is set in Cloudflare Pages secrets
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div>
          <h1>Something went wrong</h1>
          <pre>{error.toString()}</pre>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-white'} p-4`}>
      {/* Add ThreadHistory component */}
      <ThreadHistory 
        isOpen={isHistoryOpen} 
        onClose={() => setIsHistoryOpen(false)} 
        darkMode={darkMode}
        user={user}
        onSelectThread={handleThreadSelect}
      />

      {/* Show login overlay if no user and showLogin is true */}
      {!user && showLogin && (
        <LoginOverlay 
          darkMode={darkMode} 
          onClose={() => setShowLogin(false)}
        />
      )}

      {showPaymentSuccess && (
        <PaymentSuccessOverlay 
          onClose={() => setShowPaymentSuccess(false)} 
          onSuccess={fetchTokenStatus}
        />
      )}

      <div className="max-w-4xl mx-auto">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg shadow-lg`}>
          {/* Header */}
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center">
              {/* Left side: Title */}
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center gap-3`}>
                <img 
                  src={logo} 
                  alt="Logo" 
                  className={`h-12 w-12 ${!darkMode ? 'brightness-0' : ''}`} 
                />
                {import.meta.env.VITE_APP_TITLE || '.env'}
              </h1>

              {/* Right side: Controls */}
              <div className="flex items-center space-x-4">
                {/* Service selector */}
                <select
                  value={selectedService}
                  onChange={handleServiceChange}
                  className={`px-3 py-1 text-sm rounded-lg border border-blue-500 ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  {sortedServices.map(service => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>

                {/* Clear chat button */}
                <button 
                  onClick={() => setMessages([])}
                  className={`px-3 py-1 text-sm ${
                    darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  } rounded`}
                >
                  Clear Chat
                </button>

                {/* Theme toggle */}
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 text-yellow-300' : 'bg-gray-200 text-gray-700'}`}
                >
                  {darkMode ? '🌙' : '☀️'}
                </button>

                {/* Auth status and logout */}
                {user ? (
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {user.email}
                    </span>
                    <button
                      onClick={handleLogout}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        darkMode 
                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                          : 'bg-red-500 hover:bg-red-600 text-white'
                      }`}
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowLogin(true)}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      darkMode 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    Login
                  </button>
                )}

                {/* History toggle button */}
                <button
                  onClick={() => setIsHistoryOpen(true)}
                  className={`p-2 rounded-lg ${
                    darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                  title="View Chat History"
                >
                  <img 
                    src={historyIcon} 
                    alt="History" 
                    className={`w-5 h-5 ${!darkMode ? 'brightness-0' : 'brightness-0 invert'}`} 
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Chat Messages */}
          <div className={`h-[calc(70vh-80px)] overflow-y-auto p-4 space-y-4 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} ${msg.role === 'system' ? 'hidden' : ''}`}
              >
                <div 
                  className={`w-full rounded-lg p-4 ${
                    msg.role === 'user' 
                      ? 'bg-blue-500 text-white' 
                      : darkMode 
                        ? 'bg-gray-700 text-gray-200' 
                        : 'bg-gray-200 text-gray-800'
                  } relative [&_.markdown-content_pre]:bg-gray-900 [&_.markdown-content_pre]:text-gray-100 [&_.markdown-content_pre]:p-4 [&_.markdown-content_pre]:rounded-lg [&_.markdown-content_pre]:my-2 [&_.markdown-content_code]:text-gray-100`}
                >
                  <div 
                    className="markdown-content"
                    dangerouslySetInnerHTML={{ 
                      __html: (() => {
                        try {
                          return renderMarkdown(msg.content);
                        } catch (e) {
                          console.error('Markdown rendering error:', e);
                          return msg.content;
                        }
                      })()
                    }}
                  />
                  {msg.role === 'assistant' && (
                    <div className="absolute bottom-2 right-2 flex items-center gap-2">
                      <label 
                        className="flex items-center gap-1 p-1.5 rounded-md opacity-60 hover:opacity-100 transition-opacity cursor-pointer text-sm group relative"
                        title="When checked, this message will be included as context in your next prompt"
                      >
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-blue-500 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                          onChange={(e) => {
                            // TODO: Handle checkbox change
                            console.log('Include in context:', e.target.checked);
                          }}
                        />
                        <span className="text-xs">Include in context</span>
                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-2 hidden group-hover:block w-48 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg">
                          This message will be added to your next prompt as conversation context
                        </div>
                      </label>
                      <button
                        className={`p-1.5 rounded-md ${
                          darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-300'
                        }`}
                        title="Copy message"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-4 w-4" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" 
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className={`px-6 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {(!apiKey && import.meta.env.VITE_OPENAI_API_KEY !== 'PROXY') ? (
              <input
                type="password"
                placeholder="Enter your OpenAI API key"
                value={apiKey}
                onChange={(e) => handleApiKeyChange(e.target.value)}
                className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-200' 
                    : 'bg-white border-gray-300'
                }`}
              />
            ) : (
              <div className="flex space-x-3">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => user && setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                    placeholder={user ? "Type your message..." : "Click Send to see example strategy"}
                    readOnly={!user}
                    className={`w-full p-3 pr-10 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200' 
                        : 'bg-white border-gray-300'
                    }`}
                  />
                  {input && (
                    <button
                      onClick={handleClearInput}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full ${
                        user 
                          ? 'hover:bg-gray-200 dark:hover:bg-gray-600' 
                          : 'hover:bg-gray-300 dark:hover:bg-gray-500'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={isSending}
                  className={`px-6 py-3 ${
                    isSending 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            )}
          </div>

          {/* Diagnostic Panel */}
          <div className={`px-6 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`text-sm font-mono ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <div>Service: {selectedService}</div>
              <div>Status: {isSending ? '🟡 Streaming...' : '🟢 Ready'}</div>
              <div className="flex space-x-4">
                <div>Input Tokens: {tokenCounts.input}</div>
                <div>Output Tokens: {tokenCounts.output}</div>
                <div>Total Tokens: {tokenCounts.input + tokenCounts.output}</div>
              </div>
              {user && (
                <div className="mt-2 pt-2 border-t border-gray-600">
                  <TokenBar 
                    freeLimit={userTokens?.freeLimit ?? 0}
                    purchasedTokens={userTokens?.purchasedTokens ?? 0}
                    lifetimeTotal={userTokens?.lifetimeTotal ?? 0}
                    userId={user.uid}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {(userTokens?.freeLimit ?? 0).toLocaleString()} free + {(userTokens?.purchasedTokens ?? 0).toLocaleString()} purchased - {(userTokens?.lifetimeTotal ?? 0).toLocaleString()} used
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

export default App 